/**
 * El repaso de un sitio recién recibido.
 *
 * Aceptar un traspaso mueve las filas y el sitio sigue en pie, así que todo
 * PARECE terminado. No lo está: hay tres cosas que el traspaso deja rotas o a
 * medias y que nadie arregla solo, porque ninguna se anuncia en el producto.
 *
 *   · Cobros. La cuenta de Stripe se desconecta en el traspaso —es el banco de
 *     otra persona—, así que un sitio que vendía deja de poder cobrar. Se
 *     descubre cuando un cliente no puede pagar, que es el peor momento.
 *   · Identidad legal. La razón social, el domicilio y la identificación fiscal
 *     viven en el proyecto, no en la cuenta, así que VIAJAN: el aviso de
 *     privacidad y los términos del sitio siguen nombrando al dueño anterior.
 *   · Plan. El de quien recibe puede no dar para lo que acaba de llegar, y hay
 *     una gracia contada antes de que el sitio se despublique.
 *
 * ── Por qué una lista y no un asistente ─────────────────────────────────────
 *
 * Dos de las tres acciones SALEN de la aplicación —el alta de Stripe y el pago
 * del plan— y vuelven por una redirección a otra pantalla. Un asistente de pasos
 * se perdería en ese viaje y habría que reconstruirlo al volver, guardando por
 * dónde iba. Una lista que le pregunta al servidor qué falta no tiene ese
 * problema: se vuelva por donde se vuelva, enseña lo que sigue pendiente.
 *
 * Es también lo que permite dejarlo a medias sin perder nada, y por eso hay dos
 * salidas distintas: aplazar —vuelve en la siguiente visita— y darlo por
 * terminado, que es una decisión legítima y no un premio por completar los tres
 * puntos. Puede que no quiera vender, o que los datos fiscales que ya están sean
 * los suyos.
 */
import { useCallback, useEffect, useState } from 'react'
import {
  IconAlertTriangle,
  IconArrowRight,
  IconBuildingStore,
  IconCheck,
  IconCreditCard,
  IconLoader2,
  IconEyeOff,
  IconRosetteDiscountCheck,
} from '@tabler/icons-react'
import { finishHandover, getHandover, startStripeOnboarding, type Handover } from '../lib/api'
import { MascotOutlinePattern } from './MascotOutlinePattern'
import { reportPending } from '../lib/pendingNotices'
import { useI18n, translateServerError } from '../lib/i18n'
import { longDateNoYear } from '../lib/dates'
import { shortfallList } from '../lib/planLimits'

/** Aplazado en ESTA visita. Vuelve solo al recargar, que es lo que se promete. */
const SNOOZED = 'veltica:handover-snoozed'

function snoozed(projectId: string): boolean {
  try { return sessionStorage.getItem(SNOOZED) === projectId } catch { return false }
}

function snooze(projectId: string): void {
  try { sessionStorage.setItem(SNOOZED, projectId) } catch { /* modo privado */ }
}

/**
 * Cuántas cosas quedan, sin construir la lista entera.
 *
 * Hace falta antes de la primera salida del componente, y las tres condiciones
 * son las mismas que deciden los pasos de abajo. Vive aquí arriba para que no
 * puedan separarse sin que se note.
 */
function pendingCount(state: Handover | null): number {
  if (!state) return 0
  let count = 0
  if (needsPayments(state)) count += 1
  if (state.business.filled > 0) count += 1
  if (!state.plan.fits) count += 1
  return count
}

/**
 * Si los cobros son un problema, y no sólo algo que no está hecho.
 *
 * Sin nada publicado a la venta, no tener cobrador NO está roto: no hay quien
 * intente pagar. Antes bastaba con que la cuenta no estuviera conectada, así que
 * a quien recibía un sitio de puro contenido se le pedía dar de alta un negocio
 * en Stripe para nada — y encima era el único punto de la lista, o sea que la
 * entrega terminaba con una tarea inventada.
 *
 * Cuando decida vender, conectar la cuenta es el primer paso de esa sección y se
 * lo pide ella. Aquí sobra.
 */
function needsPayments(state: Handover): boolean {
  return !state.payments.connected && state.payments.sellingOffers > 0
}

type Step = {
  key: string
  icon: typeof IconCreditCard
  title: string
  body: string
  action: string
  run: () => void | Promise<void>
  /** Algo que ya está roto ahora mismo, no una tarea pendiente. */
  urgent?: boolean
}

export function ProjectHandover({ token, projectId, onOpenSection, onOpenPlans }: {
  token: string
  projectId: string
  /** Ir a una sección del taller. Las dos que se resuelven sin salir de aquí. */
  onOpenSection: (section: 'business' | 'stripe') => void
  onOpenPlans: () => void
}) {
  const { t } = useI18n()
  const [state, setState] = useState<Handover | null>(null)
  const [hidden, setHidden] = useState(() => snoozed(projectId))
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setState(await getHandover(token, projectId))
    } catch {
      /* En silencio: esto es un añadido al inicio, no el inicio. Casi nadie ha
         recibido un sitio de nadie, y llenar su pantalla de errores por una
         consulta que iba a contestar «nada» sería absurdo. */
    }
  }, [token, projectId])

  useEffect(() => { void load() }, [load])

  /* Que la pantalla sepa cuántas quedan: la mascota del saludo mira hacia aquí y
     lo dice en voz alta. Aplazado cuenta como nada — se le pidió que no
     estorbara. Ver `lib/pendingNotices`. */
  const count = pendingCount(state)
  const pending = hidden ? 0 : count
  useEffect(() => {
    reportPending('handover', pending)
    return () => { reportPending('handover', 0) }
  }, [pending])

  /*
   * Un repaso sin nada que repasar se cierra solo.
   *
   * Si no se cerrara, el servidor seguiría respondiendo «hay un traspaso a
   * medias» en cada visita al inicio, para siempre, y esta consulta se haría por
   * un aviso que nunca se pinta. Cerrarlo no pierde nada: no hay pendiente que
   * recuperar más tarde. Y si el sitio empieza a vender el mes que viene sin
   * cobrador conectado, eso ya no es un traspaso a medias — es la sección de
   * cobros pidiendo lo suyo, que es donde toca.
   *
   * Se usa `count` y no `pending`: aplazado sólo oculta el aviso de esta visita,
   * y darlo por terminado ahí lo borraría para siempre en vez de aplazarlo.
   */
  useEffect(() => {
    if (!state || count > 0) return
    void finishHandover(token, projectId).catch(() => { /* se reintenta en la siguiente visita */ })
  }, [state, count, token, projectId])

  /* Sin nada pendiente no se felicita a nadie: un recuadro que dice «no queda
     nada» ES ruido, exactamente igual que el que pedía conectar unos cobros que
     nadie necesitaba. Que no salga nada es la señal de que todo está bien. */
  if (!state || hidden || pending === 0) return null

  const connect = async () => {
    setBusy('payments')
    setError(null)
    try {
      /* El alta de Stripe se hace en Stripe: el enlace es de un solo uso, así
         que se pide justo al pulsar y se sale. Al volver, esta lista se vuelve a
         preguntar al servidor si la cuenta ya está — que es exactamente por lo
         que no es un asistente. */
      const { url } = await startStripeOnboarding(projectId, token)
      window.location.href = url
    } catch (err) {
      setError(translateServerError(err, t('err.openPaymentsSetup')))
      setBusy(null)
    }
  }

  const steps: Step[] = []

  if (needsPayments(state)) {
    const selling = state.payments.sellingOffers
    steps.push({
      key: 'payments',
      icon: IconCreditCard,
      title: t('handover.notice.payTitle'),
      body: t(selling === 1 ? 'handover.notice.payOne' : 'handover.notice.payMany', { count: selling }),
      action: t('handover.notice.payAction'),
      run: connect,
      urgent: true,
    })
  }

  /* Los datos legales se enseñan SIEMPRE que haya algo puesto, aunque esté
     completo — sobre todo si está completo: eso significa que el sitio publica
     una razón social y un domicilio que no son los tuyos. Un formulario a medias
     es un trámite; uno lleno con los datos de otro es un problema legal. */
  if (state.business.filled > 0) {
    const name = state.business.tradeName || state.business.legalName
    steps.push({
      key: 'business',
      icon: IconBuildingStore,
      title: t('handover.notice.bizTitle'),
      body: name
        ? t('handover.notice.bizNamed', { name })
        : t('handover.notice.bizUnnamed'),
      action: t('handover.notice.bizAction'),
      run: () => onOpenSection('business'),
      urgent: true,
    })
  }

  if (!state.plan.fits) {
    steps.push({
      key: 'plan',
      icon: IconRosetteDiscountCheck,
      title: t('handover.notice.planTitle'),
      body: t('handover.notice.planBody', { list: shortfallList(state.plan.shortfalls, t) })
        + (state.plan.graceUntil
          ? t('handover.notice.planGrace', { date: longDateNoYear(state.plan.graceUntil) })
          : ''),
      action: t('handover.notice.seePlans'),
      run: onOpenPlans,
      urgent: true,
    })
  }

  const dismiss = async () => {
    setBusy('done')
    try {
      await finishHandover(token, projectId)
      setState(null)
    } catch (err) {
      setError(translateServerError(err, t('err.saveThat')))
      setBusy(null)
    }
  }

  const later = () => {
    snooze(projectId)
    setHidden(true)
  }

  return (
    <section className="dash-handover dash-loaded-content-in" aria-label={t('handover.notice.aria')}>
      {/* El mismo dibujo que los banners de invitación y traspaso: esto es la
          tercera escena de la misma historia, y con otra piel se leería como una
          alerta cualquiera del panel en vez de como el final de la entrega. */}
      <MascotOutlinePattern className="dash-handover-pattern" eyes={false} />

      <header className="dash-handover-head">
        <span className="dash-handover-kicker">
          <IconCheck size={13} aria-hidden="true" />
          {t('handover.notice.yours', { name: state.projectName })}
        </span>
        <strong>
          {steps.length === 1
            ? t('handover.notice.oneLeft')
            : t('handover.notice.manyLeft', { count: steps.length })}
        </strong>
        <p>
          {t(steps.length === 1 ? 'handover.notice.oneBody' : 'handover.notice.manyBody')}
        </p>
      </header>

      {steps.length > 0 ? (
        <ul className="dash-handover-steps">
          {steps.map((step) => {
            const Icon = step.icon
            const working = busy === step.key
            return (
              <li key={step.key} className={step.urgent ? 'is-urgent' : undefined}>
                <span className="dash-handover-step-icon" aria-hidden="true">
                  {step.urgent ? <IconAlertTriangle size={15} /> : <Icon size={15} />}
                </span>
                <div className="dash-handover-step-copy">
                  <strong>{step.title}</strong>
                  <p>{step.body}</p>
                </div>
                <button
                  type="button"
                  className="dash-handover-step-btn"
                  disabled={busy !== null}
                  onClick={() => { void step.run() }}
                >
                  {working ? <IconLoader2 size={14} className="dash-spin" /> : null}
                  {working ? t('handover.notice.opening') : step.action}
                  {working ? null : <IconArrowRight size={14} aria-hidden="true" />}
                </button>
              </li>
            )
          })}
        </ul>
      ) : null}

      {error ? <p className="dash-handover-error" role="alert">{error}</p> : null}

      <div className="dash-handover-foot">
        {/* Dos decisiones distintas: «Ahora no» aplaza sólo esta visita; «No
            volver a mostrar» confirma que la persona ya entendió el pendiente
            y cierra el repaso en el servidor para todas las visitas futuras. */}
        {steps.length > 0 ? (
          <>
            <button type="button" className="dash-handover-later" disabled={busy !== null} onClick={later}>
              {t('handover.notice.later')}
            </button>
            <button type="button" className="dash-handover-dismiss" disabled={busy !== null} onClick={() => { void dismiss() }}>
              {busy === 'done' ? <IconLoader2 size={14} className="dash-spin" /> : <IconEyeOff size={14} aria-hidden="true" />}
              {t(busy === 'done' ? 'handover.notice.savingDismiss' : 'handover.notice.dismiss')}
            </button>
          </>
        ) : (
          <button type="button" className="dash-handover-done" disabled={busy !== null} onClick={() => { void dismiss() }}>
            {busy === 'done' ? <IconLoader2 size={14} className="dash-spin" /> : <IconCheck size={14} aria-hidden="true" />}
            {t('handover.notice.done')}
          </button>
        )}
      </div>
    </section>
  )
}
